<template>
  <section>
    <!-- Header Section -->
    <div class="flex bg-[#2C2ED1]">
      <div
        class="bg-[#A60304] text-white md:px-6 px-2 py-0.5 sm-max:pr-4 font-bold flex items-center text-xs sm:text-sm max-w-fit poligon-left"
        v-html="breakingNewsText">
      </div>
      <div
        class="bg-[#2C2ED1] text-white md:px-6 px-2 py-0.5 sm-max:pl-3 font-bold flex text-xs sm:text-sm items-center poligon-right"
        v-html="todayNewsText">
      </div>
    </div>

    <!-- News Ticker Section -->
    <div class="relative flex items-center overflow-hidden news-ticker-container" @mouseover="pauseTicker" @mouseleave="resumeTicker">
      <div>
        <nuxt-img
          :src="newsIconSrc"
          :alt="newsIconAlt"
          sizes="xs:92px sm:92px md:160px lg:160px xl:160px 2xl:160px"
          preload
          loading="eager"
          fetchpriority="high"
          format="webp"
          width="179"
          height="56"
          class="w-32 sm:w-48"
        />
      </div>
      <div ref="tickerWrapper" class="relative w-full -ml-2 overflow-hidden flex-nowrap">
        <ul ref="ticker" class="flex list-disc flex-nowrap font-ww-medium md:gap-x-8 gap-x-4 news-ticker">
          <li
            v-for="(item, index) in duplicatedNewsItems"
            :key="`news-item-${index}`"
            class="mr-4 text-xs sm:text-base news-item whitespace-nowrap"
            v-html="typeof item === 'object' && item.text ? item.text : item">
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup>
// Props for dynamic content
const props = defineProps({
  breakingNewsText: {
    type: String,
    default: 'BREAKING NEWS',
  },
  todayNewsText: {
    type: String,
    default: 'TODAY NEWS',
  },
  newsIconSrc: {
    type: String,
    default: '/assets/images/news-new.png',
  },
  newsIconAlt: {
    type: String,
    default: 'News Icon',
  },
  newsItems: {
    type: Array,
    default: () => [
      {
        text: "Brazil's worst drought in 91 years has devastated coffee crops"
      },
      {
        text: "Supply chain disruptions continue to drive up costs"
      },
      {
        text: "Major coffee chains announcing 15-25% price increases"
      },
      {
        text: "Changing weather patterns threatening up to 50% of coffee growing regions"
      },
      {
        text: "Growing global demand creating supply shortages"
      },
    ],
  },
});

// Process newsItems to handle both string arrays and object arrays from Builder
const processedNewsItems = computed(() => {
  if (!props.newsItems || !Array.isArray(props.newsItems)) {
    return [];
  }
  
  return props.newsItems.map(item => {
    if (typeof item === 'string') {
      return item;
    } else if (typeof item === 'object' && item !== null) {
      return item.text || '';
    }
    return '';
  }).filter(item => item !== '');
});


// Computed property for duplicated news items for seamless scrolling
const duplicatedNewsItems = computed(() => [...processedNewsItems.value, ...processedNewsItems.value]);

// Refs for DOM elements
const ticker = ref(null);
const tickerWrapper = ref(null);

let animationFrame;
let position = 0;
let isPaused = false;

const startTicker = () => {
  const tickerWidth = ticker.value.scrollWidth;

  const step = () => {
    if (!isPaused) {
      position -= 0.6; // Speed of the scrolling
      if (Math.abs(position) >= tickerWidth / 2) {
        position = 0; // Reset position for seamless scroll
      }
      ticker.value.style.transform = `translateX(${position}px)`; // Smooth scrolling effect
    }
    animationFrame = requestAnimationFrame(step);
  };
  step();
};

const pauseTicker = () => {
  isPaused = true;
};

const resumeTicker = () => {
  isPaused = false;
};

onMounted(() => {
  startTicker();
});

onBeforeUnmount(() => {
  cancelAnimationFrame(animationFrame);
});

defineBuilderComponent({
  friendlyName: 'TickersAlertsAnimations',
  inputs: [
    {
      name: 'breakingNewsText',
      type: 'richText',
      friendlyName: 'Breaking News Text',
      defaultValue: 'BREAKING NEWS',
    },
    {
      name: 'todayNewsText',
      type: 'richText',
      friendlyName: 'Today News Text',
      defaultValue: 'TODAY NEWS',
    },
    {
      name: 'newsIconSrc',
      type: 'file',
      friendlyName: 'News Icon Image',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/news-new.png',
    },
    {
      name: 'newsIconAlt',
      type: 'text',
      friendlyName: 'News Icon Alt Text',
      defaultValue: 'News Icon',
    },
    {
      name: 'newsItems',
      type: 'list',
      friendlyName: 'News Items',
      required: false,
      subFields: [
        {
          name: 'text',
          type: 'richText',
          friendlyName: 'News Item Text',
          required: true,
          defaultValue: '',
        },
      ],
      defaultValue: [
        {
          text: "Brazil's worst drought in 91 years has devastated coffee crops"
        },
        {
          text: "Supply chain disruptions continue to drive up costs"
        },
        {
          text: "Major coffee chains announcing 15-25% price increases"
        },
        {
          text: "Changing weather patterns threatening up to 50% of coffee growing regions"
        },
        {
          text: "Growing global demand creating supply shortages"
        },
      ],
    },
  ],
});
</script>

<style scoped>
.poligon-left {
  clip-path: polygon(0 0, 100% 0, 93% 100%, 0 100%);
}

.poligon-right {
  clip-path: polygon(7% 0%, 100% 0%, 100% 100%, 0% 100%);
  margin-left: -14px;
}

.news-ticker-container {
  @apply relative overflow-hidden;
}

.news-ticker {
  @apply flex will-change-transform;
}

.news-item {
  @apply whitespace-nowrap;
}
</style>
